import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { analyzeProject, type EventCard } from "@buildlab/codeview";
import type { FileMap } from "@buildlab/shared";
import { AgentPane, type PickedCard } from "./AgentPane";
import { api, type AssignmentInfo, type Checkpoint, lessonLabel } from "./api";
import { CodeCards, type CardLabel, type Watch } from "./CodeCards";
import { Editor } from "./Editor";
import { Preview } from "./Preview";
import { HistoryView } from "./HistoryView";
import { useFeature } from "./features";
import { isStudent, useMe } from "./me";
import { ProjectMap } from "./ProjectMap";
import { NotesTextBar, ProjectNotes } from "./ProjectNotes";
import { ShareButton } from "./ShowLinks";
import { ShareToClassButton } from "./ShareToClass";
import { ArenaEnterButton, isArenaProject } from "./BotArena";
import { SubmitButton } from "./SubmitDialog";
import { Back, Check, Clock, Hammer, History, MapIcon, TextSize } from "./icons";
import { ErrorScreen, Lion, LoadingScreen } from "./Lion";
import { ReadStepsToggle } from "./Narrate";
import { FullscreenTip } from "./FullscreenTip";
import { ToolsRail, WORKSPACE_TOOLS, type ToolId } from "./ToolsRail";
import { useCompact } from "./useCompact";
import "./compact.css";
// Review 17 (decision 90): mission steps (D45), tools by lesson (D46), Predict before play (D47).
import { ALL_TOOLS, type Tool } from "@buildlab/shared/steps";
import type { StepState } from "./api";
import { emitStep, useMission } from "./mission-steps";
import { PredictCover } from "./PredictCover";
import { MissionBar } from "./Mission";
import { ToolUnlock } from "./ToolUnlock";
import "./guide.css";

// The student workspace (PRD §5.1): cards, code and files on the left, the running preview in the
// middle, the helper on the right. "Project map" swaps all three for the student's visual PRD.
// Today's mission is a bar under the top bar (Mission.tsx), across all three.
// Compact layout (Review 17, C62; useCompact): the left panel folds into a tools rail and opens as
// a drawer over the game, so the game and the helper get the room on a Chromebook.

const ORDER = ["index.html", "style.css", "game.js", "PROJECT.md"];
const KIND: Record<string, string> = { html: "HTML", css: "CSS", js: "JS", md: "MD" };
const kindOf = (name: string) => KIND[name.split(".").pop()?.toLowerCase() ?? ""] ?? "FILE";

const sortFiles = (names: string[]) =>
  [...names].sort((a, b) => (ORDER.indexOf(a) === -1 ? 99 : ORDER.indexOf(a)) - (ORDER.indexOf(b) === -1 ? 99 : ORDER.indexOf(b)) || a.localeCompare(b));

export function Workspace({ projectId, onExit, large, onLarge }: { projectId: string; onExit: () => void; large: boolean; onLarge: (v: boolean) => void }) {
  const [files, setFiles] = useState<FileMap | null>(null);
  const [title, setTitle] = useState("");
  const [assignment, setAssignment] = useState<AssignmentInfo | null>(null);
  const [bot, setBot] = useState(false); // a Bot Arena bot (decision 99): Enter my bot instead of Share with class
  const [view, setView] = useState<"build" | "map" | "history">("build");
  const me = useMe();
  const showLinks = useFeature("showLinks") && !isStudent(me); // family links are the teacher's to make
  const [tab, setTabOnly] = useState<ToolId>("cards");
  const compact = useCompact();
  const [drawer, setDrawer] = useState(false); // compact layout: the left panel is open over the game
  // Anything that switches the panel also opens it (in the compact layout it's otherwise hidden).
  const setTab = useCallback((t: ToolId) => {
    setTabOnly(t);
    setDrawer(true);
  }, []);
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedCard | null>(null);
  const [draftRequest, setDraftRequest] = useState<{ text: string; nonce: number } | null>(null);
  const [codeLine, setCodeLine] = useState<{ n: number; nonce: number } | null>(null);
  const [watch, setWatch] = useState<Watch | null>(null);
  const [labels, setLabels] = useState<Record<string, CardLabel>>({});
  const [liveFile, setLiveFile] = useState<string | null>(null); // the file the helper just changed
  const labelled = useRef<Map<string, string>>(new Map()); // file -> code the labels were made for
  const [open, setOpen] = useState("game.js");
  const [notesAsText, setNotesAsText] = useState(false); // PROJECT.md opens as cards unless the student picks "Edit as text"
  const [errors, setErrors] = useState<string[]>([]);
  const [agentRunning, setAgentRunning] = useState(false);
  const [attachErrors, setAttachErrors] = useState(false);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "unsaved" | "error">("saved");
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<Map<string, string>>(new Map());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Review 17: tools by lesson, mission steps, Predict before play ----
  const [tools, setTools] = useState<Set<Tool>>(() => new Set(ALL_TOOLS));
  const [newTools, setNewTools] = useState<Tool[]>([]);
  const [stepInit, setStepInit] = useState<StepState | null>(null);
  const [coverSkipped, setCoverSkipped] = useState(false); // a teacher's one-click skip, this visit only
  const has = (t: Tool) => tools.has(t);
  // The left panel's tools (tabs on a wide screen, the rail when compact), as far as the lesson has unlocked them.
  const railTools = WORKSPACE_TOOLS.filter((t) => tools.has(t.id));
  const mission = useMission(projectId, assignment, stepInit);
  const report = mission?.report;
  useEffect(() => {
    if (!report) return;
    if (view === "map") report({ kind: "openedPanel", panel: "map" });
    else if (view === "build") report({ kind: "openedPanel", panel: tab });
  }, [view, tab, report]);
  // Play time: each second the student has the game in focus (they clicked or typed in it) counts.
  useEffect(() => {
    if (!report || view !== "build") return;
    const t = setInterval(() => {
      const el = document.activeElement;
      if (el instanceof HTMLIFrameElement && el.closest(".preview .stage") && !document.hidden) report({ kind: "played", seconds: 1 });
    }, 1000);
    return () => clearInterval(t);
  }, [report, view]);

  useEffect(() => {
    api
      .project(projectId)
      .then(({ project, assignment, tools: unlocked, newTools: fresh, steps }) => {
        const t = new Set<Tool>(unlocked ?? ALL_TOOLS);
        setTools(t);
        setNewTools(fresh ?? []);
        setStepInit(steps ?? null);
        setTabOnly((cur) => (t.has(cur) ? cur : (WORKSPACE_TOOLS.find((x) => t.has(x.id))?.id ?? cur)));
        setFiles(project.files);
        setTitle(project.title);
        setAssignment(assignment);
        setBot(isArenaProject(project.assignmentId));
        // A bot opens on bot.js, the one file that plays in the tournament.
        if (!project.files[open]) setOpen(project.files["bot.js"] !== undefined ? "bot.js" : (sortFiles(Object.keys(project.files))[0] ?? "index.html"));
      })
      .catch((e: Error) => setError(e.message));
  }, [projectId]);

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const batch = [...pending.current.entries()];
    pending.current.clear();
    if (batch.length === 0) return;
    setSaveState("saving");
    try {
      for (const [path, content] of batch) await api.saveFile(projectId, path, content);
      setSaveState(pending.current.size ? "unsaved" : "saved");
    } catch (e) {
      setSaveState("error");
      setError((e as Error).message);
    }
  }, [projectId]);

  const edit = (path: string, content: string) => {
    emitStep({ kind: "edited", file: path }); // a mission step (D45)
    setFiles((f) => (f ? { ...f, [path]: content } : f));
    pending.current.set(path, content);
    setSaveState("unsaved");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), 800);
  };

  const onErrorsChange = useCallback((e: string[]) => setErrors(e), []);

  // The code view's parse of every .js file; cheap enough to redo on each edit.
  const views = useMemo(() => (files ? analyzeProject(files) : []), [files]);
  const drawerOpen = compact && drawer && view === "build";
  const panelShown = view === "build" && railTools.length > 0 && (!compact || drawer);
  const watchNames = useMemo(
    () => (panelShown && tab === "cards" ? views.flatMap((v) => v.things.map((t) => t.name)).slice(0, 40) : []),
    [views, panelShown, tab],
  );

  // Esc closes the drawer (unless a dialog is open; it closes first).
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented && !document.querySelector("[aria-modal='true']")) setDrawer(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Friendlier card labels from one cached Haiku call per changed file (none with the demo model).
  useEffect(() => {
    if (agentRunning || (tab !== "cards" && view !== "map")) return;
    const t = setTimeout(() => {
      for (const v of views) {
        const code = files?.[v.file] ?? "";
        if (v.error || v.cards.length === 0 || labelled.current.get(v.file) === code) continue;
        labelled.current.set(v.file, code);
        const cards = v.cards.map((c) => ({ id: c.id, title: c.title, code: c.lines.map((l) => l.text).join("\n") }));
        api
          .labels(projectId, v.file, cards)
          .then(({ labels: got }) => {
            if (got.length) setLabels((all) => ({ ...all, ...Object.fromEntries(got.map((l) => [l.id, l])) }));
          })
          .catch(() => labelled.current.delete(v.file));
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [views, agentRunning, tab, view]);

  const pick = (card: EventCard, title: string, mode: "build" | "ask") => {
    setPicked({ title, file: card.file, startLine: card.startLine, endLine: card.endLine, mode, nonce: Date.now() });
    setView("build");
  };

  if (error && !files) return <ErrorScreen message={error} onHome={onExit} />;
  if (!files) return <LoadingScreen text="Opening your project…" />;

  const names = sortFiles(Object.keys(files));
  const notesOn = has("notes"); // PROJECT.md as note cards unlocks in Lesson 4 (Oct 26; was 9); before that it opens as text
  const notesOpen = notesOn && view === "build" && tab === "code" && open === "PROJECT.md" && !notesAsText && files["PROJECT.md"] !== undefined;
  const openNotes = () => {
    setOpen("PROJECT.md");
    setNotesAsText(!notesOn);
    setTab("code");
  };
  const leftTabs = railTools;
  const student = isStudent(me);
  // Lessons 1 and 2: the rest of the workspace dims a little around the step's panel (students only).
  const dim = student && !!mission?.guide && !!assignment && assignment.lesson >= 1 && assignment.lesson <= 2;
  const cover =
    assignment?.predict && mission && !mission.prediction && !coverSkipped ? (
      <PredictCover predict={assignment.predict} assignmentId={assignment.id} teacher={!student} onAnswer={(c) => mission.predict(c)} onSkip={() => setCoverSkipped(true)} />
    ) : undefined;
  return (
    <div
      className={`workspace view-${view} tab-${tab}${notesOpen ? " notes-open" : ""}${compact ? " compact" : ""}${drawerOpen ? " drawer-open" : ""}${leftTabs.length ? "" : " no-left"}${dim ? " guide-dim" : ""}${assignment ? " has-quest" : ""}`}
      data-guide={mission?.guide ?? undefined}
    >
      {newTools.length > 0 && (
        <ToolUnlock
          tools={newTools}
          onClose={() => {
            void api.toolsSeen(newTools).catch(() => {});
            setNewTools([]);
          }}
        />
      )}
      <header className="topbar">
        <button type="button" className="pill-dark back" title="Back to your projects" onClick={onExit}>
          <Back />
          <span className="bar-label">Projects</span>
        </button>
        <div className="title">
          <Lion pose="head" size={34} label="Roary the lion" />
          <span className="eyebrow gold">{me?.class?.name ?? "BuildLab"}</span>
          <h1>{assignment ? `${lessonLabel(assignment.lesson)} · ${title}` : title}</h1>
        </div>
        {/* With Large text on, these buttons show icons only; the names stay for screen readers (decision 67). */}
        <nav className="views" aria-label="Views" hidden={!has("map") && !has("history")}>
          <button type="button" title="Build" aria-current={view === "build" ? "page" : undefined} onClick={() => setView("build")}>
            <span className="bar-icon"><Hammer size={18} /></span>
            <span className="bar-label">Build</span>
          </button>
          <button type="button" title="Project map" data-view="map" hidden={!has("map")} aria-current={view === "map" ? "page" : undefined} onClick={() => setView("map")}>
            <span className="bar-icon"><MapIcon size={18} /></span>
            <span className="bar-label">Project map</span>
          </button>
          <button type="button" title="History" data-view="history" hidden={!has("history")} aria-current={view === "history" ? "page" : undefined} onClick={() => setView("history")}>
            <span className="bar-icon"><Clock size={18} /></span>
            <span className="bar-label">History</span>
          </button>
        </nav>
        {has("checkpoints") && <CheckpointMenu projectId={projectId} disabled={agentRunning} onRestore={(f) => setFiles(f)} beforeOpen={flush} />}
        {showLinks && <ShareButton projectId={projectId} title={title} disabled={agentRunning} beforeOpen={flush} />}
        {/* A bot's code stays private: it's entered in the arena, not shared to the gallery. */}
        {bot ? (
          <ArenaEnterButton projectId={projectId} title={title} disabled={agentRunning} beforeOpen={flush} />
        ) : (
          <ShareToClassButton projectId={projectId} title={title} disabled={agentRunning} beforeOpen={flush} />
        )}
        {assignment && <SubmitButton projectId={projectId} lesson={assignment.lesson} disabled={agentRunning} beforeOpen={flush} />}
        <label className="toggle" title="Large text: bigger text for the projector">
          <input id="large-text" type="checkbox" checked={large} aria-label="Large text" onChange={(e) => onLarge(e.target.checked)} />
          <span className="bar-icon"><TextSize size={18} /></span>
          <span className="bar-label">Large text</span>
        </label>
        <ReadStepsToggle />
      </header>

      {/* Today's mission, as a bar under the top bar (Mission.tsx), in every view. */}
      {assignment && <MissionBar assignment={assignment} mission={mission} />}

      {view === "map" && (
        <ProjectMap
          projectId={projectId}
          onFiles={(f) => setFiles(f)}
          files={files}
          views={views}
          labels={labels}
          title={title}
          onOpenCard={(id) => {
            setView("build");
            setTab("cards");
            setOpenCard(id);
            setTimeout(() => document.querySelector(`[data-card="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "nearest" }), 50);
          }}
          onAskHelper={(text) => {
            setView("build");
            setDraftRequest({ text, nonce: Date.now() });
          }}
        />
      )}

      {view === "history" && <HistoryView projectId={projectId} files={files} disabled={agentRunning} onRestore={(f) => setFiles(f)} />}

      {compact && view === "build" && railTools.length > 0 && (
        <ToolsRail tools={railTools} active={tab} open={drawer} onPick={(t) => (drawer && t === tab ? setDrawer(false) : setTab(t))} />
      )}

      <section className="left" id="tool-panel" aria-label="How your program works" hidden={!panelShown}>
        <div className="tabs" role={compact ? undefined : "tablist"}>
          {compact ? (
            <h2 className="drawer-title eyebrow">{railTools.find((t) => t.id === tab)?.label ?? ""}</h2>
          ) : (
            railTools.map((t) => (
              <button key={t.id} type="button" role="tab" data-tab={t.id} aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label}
              </button>
            ))
          )}
          {tab === "cards" && !agentRunning ? (
            <span className="live">
              <span className="dot" aria-hidden="true" />
              Live
            </span>
          ) : (
            <span className={`save ${saveState}`}>
              {saveState === "saved" && !agentRunning && <Check size={14} stroke="#2E7D5B" width={2.5} />}
              {agentRunning ? "Helper is editing…" : { saved: "Saved", saving: "Saving…", unsaved: "Editing…", error: "Not saved" }[saveState]}
            </span>
          )}
          {compact && (
            <button type="button" className="icon-close drawer-close" aria-label="Close (Esc)" title="Close (Esc)" onClick={() => setDrawer(false)}>
              ✕
            </button>
          )}
        </div>
        {tab === "cards" ? (
          <CodeCards
            views={views}
            labels={labels}
            watch={watch}
            errors={errors}
            disabled={agentRunning}
            picked={openCard}
            onPick={setOpenCard}
            onChange={(c, t) => pick(c, t, "build")}
            onAsk={(c, t) => pick(c, t, "ask")}
            onShowCode={(c) => {
              setOpen(c.file);
              setTab("code");
              setCodeLine({ n: c.startLine, nonce: Date.now() });
            }}
          />
        ) : tab === "files" ? (
          <>
            <ul className="file-list">
              {names.map((n) => (
                <li key={n}>
                  <button
                    type="button"
                    className={`${n === open ? "on" : ""}${n === liveFile ? " live-edit" : ""}`}
                    onClick={() => {
                      if (n === "PROJECT.md") return openNotes();
                      setOpen(n);
                      setTab("code");
                    }}
                  >
                    <span className={`fkind k-${kindOf(n).toLowerCase()}`} aria-hidden="true">
                      {kindOf(n)}
                    </span>
                    <span className="fname">{n}</span>
                    <span className="fsize">{(new TextEncoder().encode(files[n]!).length / 1024).toFixed(1)} KB</span>
                  </button>
                </li>
              ))}
            </ul>
            {files["PROJECT.md"] !== undefined && notesOn && (
              <div className="notes-tip">
                <span className="eyebrow">Project notes</span>
                <p>PROJECT.md is your helper's memory. Add rules there and it follows them every time.</p>
                <button type="button" className="outline small open-notes" onClick={openNotes}>
                  Open my notes
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="code">
            <div className="code-tabs">
              {names.map((n) => (
                <button key={n} type="button" className={n === open ? "on" : ""} onClick={() => setOpen(n)}>
                  {n}
                </button>
              ))}
            </div>
            {notesOpen ? (
              <ProjectNotes
                files={files}
                readOnly={agentRunning}
                onChange={(md) => edit("PROJECT.md", md)}
                onEditAsText={() => setNotesAsText(true)}
                projectId={projectId}
                beforeCheck={flush}
                onFiles={(f) => setFiles(f)}
                onBugReport={(text) => setDraftRequest({ text, nonce: Date.now() })}
              />
            ) : (
              <>
                {open === "PROJECT.md" && notesOn && <NotesTextBar onBack={() => setNotesAsText(false)} />}
                {files[open] !== undefined && <Editor path={open} value={files[open]!} readOnly={agentRunning} onChange={(v) => edit(open, v)} line={codeLine} />}
              </>
            )}
          </div>
        )}
      </section>

      {view === "build" && (
      <Preview
        projectId={projectId}
        files={files}
        watchNames={watchNames}
        onWatch={setWatch}
caption={agentRunning ? (liveFile ? `Updating as the helper edits ${liveFile}` : "The helper is working…") : tab === "cards" && panelShown && has("cards") ? "Playing · cards light up as they run" : undefined}
        compact={compact}
        banner={compact ? <FullscreenTip /> : undefined}
        cover={cover}
        errorDetails={has("console")}
        onErrorsChange={onErrorsChange}
        onSendErrors={
          agentRunning
            ? undefined
            : () => {
                setAttachErrors(true);
                document.getElementById("message")?.focus();
              }
        }
      />
      )}

      <div className="agent-wrap" hidden={view !== "build"}>
      <AgentPane
        projectId={projectId}
        picked={picked}
        onClearPicked={() => setPicked(null)}
        draftRequest={draftRequest}
        assignment={assignment}
        bot={bot}
        allowedModes={student && assignment ? assignment.modes.filter((m) => has(m)) : undefined} // students see only unlocked modes (D46); teachers see every mode
        errors={errors}
        attachErrors={attachErrors}
        onAttachErrors={setAttachErrors}
        onFiles={(f) => setFiles(f)}
        onTurnStart={flush}
        onRunningChange={(running) => {
          setAgentRunning(running);
          if (!running) setLiveFile(null);
        }}
        onFileChange={(path, content) => {
          // Work in progress: the preview, code and cards follow each edit as it happens.
          setFiles((f) => {
            if (!f) return f;
            const next = { ...f };
            if (content === null) delete next[path];
            else next[path] = content;
            return next;
          });
          setLiveFile(path);
        }}
      />
      </div>
    </div>
  );
}

function CheckpointMenu({ projectId, disabled, onRestore, beforeOpen }: { projectId: string; disabled: boolean; onRestore: (f: FileMap) => void; beforeOpen: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState<Checkpoint[]>([]);
  const [confirm, setConfirm] = useState<number | null>(null);

  const load = async () => {
    await beforeOpen();
    setList((await api.checkpoints(projectId)).checkpoints);
  };

  return (
    <div className="menu">
      <button
        type="button"
        className="pill-dark"
        data-step-panel="checkpoints"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          setConfirm(null);
          if (!open) void load();
        }}
      >
        <History />
        <span className="bar-label">Checkpoints</span>
      </button>
      {open && (
        <div className="menu-panel">
          <div className="row between">
            <b>Save points</b>
            <button
              type="button"
              className="linklike"
              onClick={async () => {
                await api.saveCheckpoint(projectId, "Save point");
                emitStep({ kind: "savePoint" }); // a mission step (D45)
                await load();
              }}
            >
              + Make one now
            </button>
          </div>
          {list.length === 0 && <p className="muted small">Checkpoints appear here before each change the helper makes.</p>}
          <ol>
            {list.map((c) => (
              <li key={c.id}>
                <span className="reason">{c.reason}</span>
                <span className="when">{new Date(c.createdAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                {confirm === c.id ? (
                  <span className="row">
                    <button
                      type="button"
                      className="primary small"
                      onClick={async () => {
                        const { files } = await api.restore(projectId, c.id);
                        onRestore(files);
                        setOpen(false);
                      }}
                    >
                      Restore
                    </button>
                    <button type="button" className="linklike" onClick={() => setConfirm(null)}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" className="linklike" onClick={() => setConfirm(c.id)}>
                    Go back to this
                  </button>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
